import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck, MapPin } from "lucide-react";
import { AREAS, CERTS, COMPLIANCE, SECTORS, Service, SERVICES } from "@/data/site";
import { useSiteContent } from "@/lib/content";
import QuoteForm from "./QuoteForm";
import ReviewWidget from "./ReviewWidget";
import { CallButton } from "./Cta";

export function SectionHead({ eyebrow, title, sub }: { eyebrow: string; title: string; sub?: string }) {
  return (
    <div className="mb-12 max-w-3xl">
      <p className="eyebrow mb-3">{eyebrow}</p>
      <h2 className="text-3xl font-bold uppercase leading-none tracking-tight text-white sm:text-4xl lg:text-5xl">{title}</h2>
      {sub && <p className="mt-4 text-base text-slate-400 md:text-lg">{sub}</p>}
    </div>
  );
}

export function CertStrip() {
  return (
    <div className="border-y border-slate-800 bg-[#161920]" data-testid="cert-strip">
      <div className="mx-auto grid max-w-7xl grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
        {CERTS.map((c) => (
          <div key={c.code} className="flex items-center gap-3 border-slate-800 px-5 py-5 [&:not(:last-child)]:border-r">
            <ShieldCheck className="h-5 w-5 shrink-0 text-brand-500" />
            <div>
              <div className="font-display text-lg font-bold leading-none text-white">{c.code}</div>
              <div className="text-xs text-slate-400">{c.label}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ServicesGrid({ items = SERVICES, exclude }: { items?: Service[]; exclude?: string }) {
  const list = items.filter((s) => s.slug !== exclude);
  return (
    <div className="grid gap-px bg-slate-800 md:grid-cols-2">
      {list.map((s, i) => (
        <Link
          key={s.slug}
          to={`/services/${s.slug}`}
          data-testid={`service-card-${s.slug}`}
          className="group relative flex min-h-[320px] flex-col justify-end overflow-hidden bg-[#0F1115] p-8"
        >
          <img src={s.image} alt={s.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-30 transition-[opacity,transform] duration-700 group-hover:scale-105 group-hover:opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] via-[#0F1115]/70 to-transparent" />
          <div className="relative">
            <span className="font-mono text-xs text-brand-500">0{i + 1}</span>
            <h3 className="mt-2 flex items-start justify-between gap-4 text-3xl font-bold uppercase text-white">
              {s.title}
              <ArrowUpRight className="h-7 w-7 shrink-0 text-brand-500 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
            </h3>
            <p className="mt-3 max-w-md text-sm text-slate-300">{s.short}</p>
          </div>
        </Link>
      ))}
    </div>
  );
}

export function SectorsGrid() {
  return (
    <div className="grid grid-cols-2 gap-px bg-slate-800 lg:grid-cols-3">
      {SECTORS.map((s) => (
        <div key={s.name} className="bg-[#161920] p-6 md:p-8">
          <h3 className="text-xl font-bold uppercase text-white md:text-2xl">{s.name}</h3>
          <p className="mt-1 font-mono text-xs text-slate-400">{s.note}</p>
        </div>
      ))}
    </div>
  );
}

export function AreasGrid({ exclude }: { exclude?: string }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
      {AREAS.filter((a) => a.slug !== exclude).map((a) => (
        <Link key={a.slug} to={`/areas/${a.slug}`} data-testid={`area-card-${a.slug}`} className="group border border-slate-800 bg-[#161920] p-5 transition-colors hover:border-brand-500">
          <MapPin className="h-5 w-5 text-brand-500" />
          <h3 className="mt-3 text-xl font-bold uppercase text-white">{a.name}</h3>
          <p className="font-mono text-xs text-slate-500">{a.state || "QLD"} {a.postcode}</p>
          <p className="mt-3 text-xs text-slate-400">{a.industries.slice(0, 2).join(" · ")}</p>
        </Link>
      ))}
    </div>
  );
}

export function GalleryStrip() {
  const { gallery } = useSiteContent();
  if (!gallery.length) return null;
  return (
    <div data-testid="home-gallery">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {gallery.slice(0, 6).map((g, i) => (
          <Link to="/projects" key={g.src + i} className="group relative overflow-hidden border border-slate-800" data-testid={`home-gallery-item-${i}`}>
            <img src={g.src} alt={`${g.title} — ${g.sector}${g.suburb ? `, ${g.suburb}` : ""}`} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 to-transparent p-3 font-display text-lg font-bold uppercase text-white">{g.title}</span>
          </Link>
        ))}
      </div>
      <Link to="/projects" className="btn-ghost mt-6" data-testid="home-gallery-link">View full gallery & case studies <ArrowUpRight className="h-5 w-5" /></Link>
    </div>
  );
}

export function CaseStudies({ limit = 3 }: { limit?: number }) {
  const { caseStudies } = useSiteContent();
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {caseStudies.slice(0, limit).map((c, i) => (
        <article key={c.title} data-testid={`case-study-${i}`} className="flex flex-col border border-slate-800 bg-[#161920]">
          <img src={c.image} alt={`${c.title} ${c.location}`} loading="lazy" className="aspect-[16/10] w-full object-cover" />
          <div className="flex flex-1 flex-col p-6">
            <p className="eyebrow">{c.location} · {c.service}</p>
            <h3 className="mt-2 text-2xl font-bold uppercase text-white">{c.title}</h3>
            <div className="mt-4 grid grid-cols-2 gap-px bg-slate-800">
              <div className="bg-[#0F1115] p-3"><div className="font-display text-2xl font-bold text-brand-500">{c.area}</div><div className="text-xs text-slate-500">Floor area</div></div>
              <div className="bg-[#0F1115] p-3"><div className="font-display text-2xl font-bold text-brand-500">{c.duration}</div><div className="text-xs text-slate-500">On-site</div></div>
            </div>
            <dl className="mt-4 space-y-3 text-sm">
              <div><dt className="font-semibold text-slate-200">Challenge</dt><dd className="text-slate-400">{c.challenge}</dd></div>
              <div><dt className="font-semibold text-slate-200">Solution</dt><dd className="text-slate-400">{c.solution}</dd></div>
              <div><dt className="font-semibold text-slate-200">Result</dt><dd className="text-slate-300">{c.result}</dd></div>
            </dl>
          </div>
        </article>
      ))}
    </div>
  );
}

export function Testimonials() {
  return <ReviewWidget />;
}

export function Compliance() {
  return (
    <div className="grid gap-px bg-slate-800 md:grid-cols-2">
      {COMPLIANCE.map((c) => (
        <div key={c.title} className="bg-[#0F1115] p-6 md:p-8">
          <h3 className="text-xl font-bold uppercase text-white">{c.title}</h3>
          <p className="mt-2 text-sm text-slate-400">{c.body}</p>
        </div>
      ))}
    </div>
  );
}

export function QuoteSection({ title = "Get a commercial flooring quote", workType, location }: { title?: string; workType?: string; location?: string }) {
  return (
    <section id="quote" className="scroll-mt-20 border-t border-slate-800 bg-[#161920] grid-lines" data-testid="quote-section">
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-20 md:px-8 lg:grid-cols-[1fr_1.4fr] lg:py-28">
        <div>
          <p className="eyebrow mb-3">Talk to an estimator</p>
          <h2 className="text-4xl font-bold uppercase leading-none text-white lg:text-5xl">{title}</h2>
          <p className="mt-5 text-slate-400">Fastest response is a phone call — speak directly with an estimator, not a call centre. Or send your details and we'll come back within one business day.</p>
          <div className="mt-8"><CallButton id="quote-section-call" /></div>
          <ul className="mt-8 space-y-2 font-mono text-xs text-slate-400">
            <li>— Free site inspection for projects 100 m²+</li>
            <li>— Itemised quote within 48 hours of inspection</li>
            <li>— Night & weekend programs available</li>
          </ul>
        </div>
        <div className="border border-slate-700 bg-[#0F1115] p-6 md:p-8">
          <QuoteForm defaultWorkType={workType} defaultLocation={location} />
        </div>
      </div>
    </section>
  );
}

export function Faqs({ items }: { items: { q: string; a: string }[] }) {
  return (
    <div className="divide-y divide-slate-800 border-y border-slate-800">
      {items.map((f, i) => (
        <details key={f.q} className="group py-5" data-testid={`faq-${i}`}>
          <summary className="flex cursor-pointer list-none items-center justify-between text-lg font-semibold text-white">
            {f.q}
            <span className="font-mono text-brand-500 transition-transform group-open:rotate-45">+</span>
          </summary>
          <p className="mt-3 text-slate-400">{f.a}</p>
        </details>
      ))}
    </div>
  );
}
